import Link from 'next/link';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import cookieCutter from 'cookie-cutter';
import dynamic from 'next/dynamic';
import { Field, Formik } from 'formik';
import { NextRouter, useRouter } from 'next/router';
import { SiteLinksSearchBoxJsonLd } from 'next-seo';
import { useMediaQuery, useTheme } from '@mui/material';

import {
  hexToRgb,
  internationalizationToLanguages,
  isBrowser,
  rgbDataURL,
  toKebabCaseWithAccentsCyrillicCJK,
} from 'corporate-utils';
import {
  availableInternationalization,
  defaultInternationalization,
  notCurrentAvailableInternationalization,
  notCurrentAvailableInternationalizationPROD,
  priorityInternationalization,
} from 'corporate-types';

import Autocomplete, { ListSearch } from '../../autocomplete/Autocomplete';
import LinkActionMegaMenu from '../../link-action-megamenu/LinkActionMegaMenu';
import LinkActionSubmenu from '../../link-action-submenu/LinkActionSubmenu';
import LocalizationModal from '../../localization-modal/LocalizationModal';
import LocalizationSelect from '../../localization-select/LocalizationSelect';
import MenuMobileModal from '../../mobile-menu/mobile-menu-modal/MobileMenuModal';
import ReservedAreaModal from '../../restricted-area-modal/ReservedAreaModal';
import SearchModal from '../../search-modal/SearchModal';
import { LinkType } from '../../link-action/LinkAction';

import { Category, MegamenuLink } from '../../../models/domain/interfaces';
import { Link as LinkMenuType } from '../../../models/auto-generated-strapi-types/types/components/Link';
import { ReservedArea } from '../../../models/auto-generated-strapi-types/types/components/ReservedArea';
import { SelectType, TextSize } from '../../../models/enum';

import { KeyboardArrowDownIcon, PersonIcon, SearchIcon } from '../../../assets';

import { getAvailableStringFromCategoryByLocale } from '../../../utils/categories';
import { getSearchResults, getSectorUI } from '../../../utils/api';
import { getStrapiMedia } from '../../../utils/media';
import { mapToSiteLinksSearchBoxJsonLd } from '../../../utils/schemas';
import { urlFromCategoriesIds } from '../../../utils/urls';

import {
  MegaMenuContainer,
  HeaderContainer,
  LinkLogo,
  Logo,
  MainMenu,
  Pages,
  SectionCenter,
  SectionLeft,
  SectionRight,
  StyledContentWrapper,
  StyledForm,
  StyledMenuIcon,
  SubMenu,
  RightMobileMenuStyled,
  LeftMobileMenuStyled,
  SecondBar,
  FirstBar,
  FirstBarContainer,
  ThirdBar,
  SectionSearch,
  LinkLogoStyled,
  LogoDesktopStyeld,
  SecondBarSectionLeft,
  SectionLogoCenter,
} from './Header.style';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { LocalizedPath, LocalizedPaths } from '../../../models/domain/type';
import { DEFAULT_IMAGE } from '../../../models/domain/const';
import ImageWithFallback from '../../image-with-fallback/ImageWithFallback';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import {
  callApiToVerifyCaptcha,
  getUseAlternativeCaptcha,
} from '../../../utils/captcha';

const MegaMenu = dynamic(() => import('../../megamenu/Megamenu'));
const MobileBar = dynamic(() => import('./Header.style'));

interface Link {
  url: string;
  text: string;
  idDwh?: string;
}

interface ResultSearch {
  id: string;
  type: string;
  name: string;
  nameLanguage: any;
  children: string[];
  tags: string[];
  technical_data: string[];
  parents?: number[];
  categoriesCalculated?: number[];
}

interface HeaderProps {
  logo: any;
  mainLinks: LinkMenuType[];
  megamenuLinks: MegamenuLink[];
  locale: string;
  translations: { [key: string]: any };
  localizedPaths: LocalizedPaths;
  reservedArea: ReservedArea;
  onMouseOver: (
    idDwh: string,
    indexSection: number,
    cover: any,
    recommendedProduct: any
  ) => void;
  onMouseClick: () => void;
  userCountry: string;
  mainLinksFontSize?: number;
  mainLinksMobileFontSize?: number;
  mainLinksFontWeight?: number;
  mainLinksMobileFontWeight?: number;
  megamenuLinksFontSize?: number;
  megamenuLinksMobileFontSize?: number;
  megamenuLinksFontWeight?: number;
  megamenuLinksMobileFontWeight?: number;
  megamenuMaxWidthDesktop?: number;
  isPrerenderRequest: boolean;
  blockSearch?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  logo,
  mainLinks,
  megamenuLinks,
  locale,
  translations,
  localizedPaths,
  reservedArea,
  mainLinksFontSize,
  mainLinksMobileFontSize,
  mainLinksFontWeight,
  megamenuLinksFontSize,
  megamenuLinksMobileFontSize,
  megamenuLinksFontWeight,
  megamenuLinksMobileFontWeight,
  mainLinksMobileFontWeight,
  megamenuMaxWidthDesktop,
  userCountry,
  isPrerenderRequest,
  blockSearch,
}: HeaderProps) => {
  const [open, setOpen] = useState(false);
  const [megaMenuMainCategory, setMegaMenuMainCategory] = useState<any>();
  const [search, setSearch] = useState('');
  const [sectorPath, setSectorPath] = useState<{ [key: string]: string }>({});
  const [result, setResult] = useState<ListSearch | undefined>(undefined);
  const [request, setRequest] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openLocalizationModal, setOpenLocalizationModal] = useState(false);
  const [openSearchModal, setOpenSearchModal] = useState(false);
  const subMenuRef = useRef();
  const router = useRouter();
  const { query } = router;
  const hover = useRef<any | undefined>();
  const exitHover = useRef<any | undefined>();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpointsMediaQuery.up('lg'));
  const isMobile = useMediaQuery(theme.breakpointsMediaQuery.down('lg'));
  const [, setRerender] = useState<number>(0); // Trick to force re-render
  const { executeRecaptcha } = useGoogleReCaptcha();
  const hCaptchaHeaderRef = React.useRef<HCaptcha>(null);
  const useAlternativeCaptcha = getUseAlternativeCaptcha(userCountry);
  const handleCloseModal = () => setOpenModal(false);

  const handleCloseLocalizationModal = () => setOpenLocalizationModal(false);

  const handleOpenSearchnModal = () => setOpenSearchModal(true);
  const handleCloseSearchModal = () => setOpenSearchModal(false);

  const downloadSectorPath = useCallback(
    async (resTransformed: ListSearch): Promise<void> => {
      if (resTransformed['sector']) {
        const onlySector = resTransformed['sector'];

        const promiseAll = await Promise.all(
          onlySector.map(async ({ value }) => {
            return (
              sectorPath[`${value}_${locale}`] ||
              (await getSectorUI({ idDwh: parseInt(value), locale }))
            );
          })
        );

        const copy = { ...sectorPath };

        promiseAll.forEach((result: any) => {
          const { attributes } = result || {};
          const { slug, idDwh } = attributes || {};

          if (slug && idDwh && locale && !copy[idDwh]) {
            copy[`${idDwh}_${locale}`] = slug;
          }
        });
        setSectorPath(copy);
      }
    },
    [locale, sectorPath]
  );

  const getSearch = async (search: string): Promise<void> => {
    setIsLoading(true);
    if (search.length >= 3) {
      const token = (await executeRecaptcha?.('search')) || '';

      if (token) {
        const res = await getSearchResults(
          search,
          {
            token,
            alternativeCaptcha: useAlternativeCaptcha,
          },
          locale
        );
        const resTransformed = searchTransform(res);
        setResult(resTransformed);
        downloadSectorPath(resTransformed);
      }
    } else {
      setResult(undefined);
    }
    setIsLoading(false);
  };

  const updateAutocomplete = async (search: string): Promise<void> => {
    setSearch(search);
    if (search.length >= 3 && !useAlternativeCaptcha) {
      if (request) {
        clearTimeout(request);
      }
      setRequest(setTimeout(() => getSearch(search), 300));
    } else {
      setResult(undefined);
    }
  };

  const searchTransform = (results: ResultSearch[]): ListSearch => {
    const list: ListSearch = {};
    for (const result of results) {
      if (!list[result.type]) list[result.type] = [];
      if (result.type === 'category' || result.type === 'sector') {
        const text =
          router.locale !== 'default'
            ? result.nameLanguage[
                internationalizationToLanguages(
                  (router.locale as string) || defaultInternationalization,
                  router?.asPath as string
                ) as string
            ] ||
              result.nameLanguage[
                internationalizationToLanguages(
                  priorityInternationalization[0] ||
                    defaultInternationalization,
                  router?.asPath as string
                ) as string
              ] ||
              result.nameLanguage[
                internationalizationToLanguages(
                  priorityInternationalization[1] ||
                    defaultInternationalization,
                  router?.asPath as string
                ) as string
              ]
            : result.nameLanguage[
                internationalizationToLanguages(
                  defaultInternationalization,
                  router?.asPath as string
                ) as string
            ];
        list[result.type].push({
          text,
          value: result.id,
          parents: result.parents,
        });
      } else
        list[result.type].push({
          text: result.name,
          value: result.id,
          parents: result.parents,
        });
    }
    return list;
  };

  const onChangeLanguage = (language: string) => {
    const path = localizedPaths?.find?.(
      (path: LocalizedPath) => path?.locale == language
    ) || {
      href: '/',
    };
    cookieCutter.set('NEXT_LOCALE', language, {
      path: '/',
    });
    if (path.href == '') path.href = '/';
    delete query?.slug;

    const queryTranslated: any = {};

    Object.keys(query).forEach((key: string) => {
      const translationFound = translations.keys.find(
        ({ keysTranslated }: { keysTranslated: string[] }) => {
          return keysTranslated.indexOf(key) !== -1;
        }
      );

      if (translationFound && translationFound.valueTranslated[language]) {
        queryTranslated[translationFound.valueTranslated[language]] =
          query[key];
      } else {
        queryTranslated[key] = query[key];
      }
    });

    router.push(
      {
        pathname: path.href,
        query: path.href != '/' ? queryTranslated : null,
      },
      {
        pathname: path.href,
        query: path.href != '/' ? queryTranslated : null,
      },
      { locale: language }
    );
  };

  const verifyHCaptchaToken = async (): Promise<boolean> => {
    try {
      const token = await hCaptchaHeaderRef?.current
        ?.execute({ async: true })
        .then(({ response }) => {
          return response;
        })
        .catch((err) => {
          console.error(err);
        });

      const res = await callApiToVerifyCaptcha(
        token as string,
        useAlternativeCaptcha,
        locale
      );

      return res.status === 200;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const onClickSuggestion = async (
    value: string,
    type: string,
    parents: number[],
    text: string
  ): Promise<void> => {
    let userVerified: boolean;
    switch (type) {
      case 'product': {
        let url = urlFromCategoriesIds(
          megamenuLinks,
          parents,
          router.locale,
          'product',
          { sku: value, nameFather: text },
          'packages/corporate-ui/src/components/shell/header/Header.tsx 1'
        );
        if (!url) url = '/';
        router.push(
          {
            pathname: url,
          },
          {
            pathname: url,
          },
          { locale: router.locale }
        );
        break;
      }
      case 'category': {
        let url = urlFromCategoriesIds(
          megamenuLinks,
          parents,
          router.locale,
          parents.length > 1 ? 'category' : 'category_father',
          undefined,
          'packages/corporate-ui/src/components/shell/header/Header.tsx 2'
        );
        if (!url) url = '/';
        if (parents.length > 2)
          query[translations?.queryParmas?.subcategory] =
            '' + parents[parents.length - 1];
        else delete query?.[translations?.queryParmas?.subcategory];
        delete query?.slug;
        router.push(
          {
            pathname: url,
            query: url != '/' ? query : null,
          },
          {
            pathname: url,
            query: url != '/' ? query : null,
          },
          { locale: router.locale }
        );
        break;
      }
      case 'sector':
        router.push(
          {
            pathname: sectorPath[`${value}_${locale}`],
          },
          {
            pathname: sectorPath[`${value}_${locale}`],
          },
          { locale: router.locale }
        );
        break;
      case 'manualSearch':
        userVerified = useAlternativeCaptcha
          ? await verifyHCaptchaToken()
          : true;
        userVerified &&
          router.push(
            {
              pathname: translations.urls.searchProduct,
              query: { q: value },
            },
            {
              pathname: translations.urls.searchProduct,
              query: { q: value },
            },
            { locale: router.locale }
          );
        break;
      default:
        break;
    }
    setSearch('');
  };

  const checkIfCurrentActiveSlugIsAMegamenuLink = (
    link: MegamenuLink,
    router: NextRouter
  ) => {
    const pathSplitted = router.asPath.split('/');

    return pathSplitted.length > 0 &&
      (
        (toKebabCaseWithAccentsCyrillicCJK(link.text || '') as string) || ''
      )?.toLowerCase() === pathSplitted[1]
      ? 'active'
      : '';
  };

  const closeMenuOnResize = useCallback(() => {
    setOpen(false);
    window.focus();
  }, []);

  // --------------------------------------------------------------------------
  // useEffects
  // --------------------------------------------------------------------------

  useEffect(() => {
    setRerender(Date.now());
  }, []);

  // When user changes page, reset the selection
  useEffect(
    () => {
      setMegaMenuMainCategory('');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router]
  );

  // When user opens the menu, fix the scroll bar flash
  useEffect(
    () => {
      const firstDiv = isBrowser() && document.getElementsByTagName('body')[0];
      const scrollFixerManual =
        isBrowser() && document.getElementById('manual-scroll-fixer');

      if (open) {
        if (firstDiv && scrollFixerManual) {
          firstDiv.style.overflowY = 'hidden';
          scrollFixerManual.style.display = 'block';
        }
      } else {
        if (firstDiv && scrollFixerManual) {
          firstDiv.style.overflowY = 'auto';
          scrollFixerManual.style.display = 'none';
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  // When the component load the first time
  useEffect(
    () => {
      isBrowser() && window.addEventListener('resize', closeMenuOnResize);
      return () => {
        isBrowser() && window.removeEventListener('resize', closeMenuOnResize);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <HeaderContainer>
      <StyledContentWrapper
        hasMegamenu={megamenuLinks.length > 0}
        isMobile={isMobile}
      >
        {/* Desktop */}
        {(typeof window === 'undefined' || isDesktop) && (
          <>
            <FirstBar>
              <FirstBarContainer>
                <SectionRight>
                  <MainMenu open={open}>
                    <Pages>
                      {mainLinks?.map?.((link: LinkMenuType, index: number) => (
                        <LinkActionSubmenu
                          key={`linkaction_${index}`}
                          href={link.url}
                          text={link.text}
                          title={link.text}
                          type={LinkType.header}
                          size={TextSize.small}
                          subLinks={link.subLink}
                          last={index === mainLinks.length - 1}
                          fontWeight={mainLinksFontWeight}
                          mobileFontWeight={mainLinksMobileFontWeight}
                          fontSize={mainLinksFontSize}
                          mobileFontSize={mainLinksMobileFontSize}
                        />
                      ))}
                    </Pages>
                  </MainMenu>
                </SectionRight>
              </FirstBarContainer>
            </FirstBar>

            <SecondBar>
              <SecondBarSectionLeft>
                <Link
                  href={`/${locale ? locale : defaultInternationalization}`}
                  passHref
                >
                  <LinkLogoStyled>
                    <LogoDesktopStyeld>
                      <ImageWithFallback
                        layout="fill"
                        objectFit="cover"
                        placeholder="blur"
                        blurDataURL={rgbDataURL(
                          hexToRgb('ffffff')?.[0],
                          hexToRgb('ffffff')?.[1],
                          hexToRgb('ffffff')?.[2]
                        )}
                        src={getStrapiMedia(logo?.data?.attributes?.url ?? '')}
                        alt={logo?.data?.attributes?.alternativeText}
                        prefetch={false}
                        fallbackSrc={DEFAULT_IMAGE}
                        isPrerenderRequest={isPrerenderRequest}
                      />
                    </LogoDesktopStyeld>
                  </LinkLogoStyled>
                </Link>
              </SecondBarSectionLeft>

              {!blockSearch && (
                <SectionSearch>
                  <Formik
                    initialValues={{
                      category: '',
                      search: '',
                    }}
                    onSubmit={() => {}}
                  >
                    {(): React.ReactNode => (
                      <StyledForm>
                        <Field
                          name="search"
                          component={Autocomplete}
                          updateSearch={updateAutocomplete}
                          search={search}
                          loading={isLoading}
                          results={result}
                          placeholder={
                            translations?.components?.shell?.header?.search
                          }
                          onClickSuggestion={onClickSuggestion}
                          useAlternativeHint={useAlternativeCaptcha}
                        />
                        {useAlternativeCaptcha && (
                          <HCaptcha
                            sitekey={
                              process.env.NEXT_PUBLIC_H_CAPTCHA_SITE_KEY || ''
                            }
                            size="invisible"
                            ref={hCaptchaHeaderRef}
                            reCaptchaCompat={false}
                          />
                        )}
                      </StyledForm>
                    )}
                  </Formik>
                </SectionSearch>
              )}

              <SectionRight>
                <LinkActionSubmenu
                  text={reservedArea?.text || ''}
                  title={reservedArea?.text || ''}
                  type={LinkType.headerSecondary}
                  size={TextSize.small}
                  icon={<PersonIcon />}
                  subLinks={reservedArea?.subLink}
                />
                <LocalizationSelect
                  onChange={(val: string | number) =>
                    onChangeLanguage(val.toString())
                  }
                  currentValue={locale}
                  options={
                    Object.values(availableInternationalization)
                      .filter((value) => {
                        return process.env.NEXT_PUBLIC_ENV === 'PROD'
                          ? Object.values(
                            notCurrentAvailableInternationalizationPROD
                          ).indexOf(value) === -1
                          : Object.values(
                            notCurrentAvailableInternationalization
                          ).indexOf(value) === -1;
                      })
                      .map((internationalization: string) => {
                        return {
                          label:
                            translations?.components?.shell?.header?.languages[
                              internationalization
                            ],
                          value: internationalization,
                        };
                      }) || []
                  }
                  type={SelectType.third}
                  textSize={TextSize.medium}
                />
              </SectionRight>
            </SecondBar>

            {megamenuLinks.length > 0 && (
              <ThirdBar megamenuMaxWidthDesktop={megamenuMaxWidthDesktop}>
                <SectionCenter>
                  {/* Menu links */}
                  <SubMenu
                    open={open}
                    onTouchStart={() => {
                      setMegaMenuMainCategory('');
                    }}
                  >
                    {megamenuLinks?.map?.(
                      (link: MegamenuLink, index: number) => {
                        const father = link?.firstChild?.find?.(
                          (category: Category) => category.depth === 0
                        );

                        return (
                          <LinkActionMegaMenu
                            hover={hover}
                            exitHover={exitHover}
                            key={`linkaction_megamenu_${index}`}
                            identify={index}
                            href={link.url}
                            text={
                              link.text ||
                              (father &&
                                getAvailableStringFromCategoryByLocale(
                                  father,
                                  locale,
                                  'packages/corporate-ui/src/components/shell/header/Header.tsx'
                                )) ||
                              ''
                            }
                            icon={
                              link.idDwh ? <KeyboardArrowDownIcon /> : undefined
                            }
                            title={link.text}
                            noClick={true}
                            isActive={checkIfCurrentActiveSlugIsAMegamenuLink(
                              link,
                              router
                            )}
                            onMouseOver={(e: Event) => {
                              subMenuRef.current = (e?.target as any) || '';
                              setMegaMenuMainCategory(link?.idDwh || '');
                            }}
                            onMouseLeave={(e: any, touch = false) => {
                              const { top } =
                                e?.target?.getBoundingClientRect?.();

                              if (e.clientY <= top || touch) {
                                setMegaMenuMainCategory('');
                              }
                            }}
                            type={LinkType.header}
                            size={TextSize.medium}
                            last={index === megamenuLinks.length - 1}
                            fontWeight={megamenuLinksFontWeight}
                            mobileFontWeight={megamenuLinksMobileFontWeight}
                            fontSize={megamenuLinksFontSize}
                            mobileFontSize={megamenuLinksMobileFontSize}
                          />
                        );
                      }
                    )}
                  </SubMenu>
                </SectionCenter>
              </ThirdBar>
            )}
          </>
        )}

        {/* Mobile */}
        {typeof window !== 'undefined' && isMobile && (
          <MobileBar>
            <SectionLeft>
              <LeftMobileMenuStyled>
                <StyledMenuIcon
                  sx={{ fontSize: 15 }}
                  onClick={() => setOpen(true)}
                />
                {!blockSearch && (
                  <SearchIcon onClick={handleOpenSearchnModal} />
                )}
              </LeftMobileMenuStyled>
            </SectionLeft>

            <SectionLogoCenter>
              <Link href="/" passHref>
                <LinkLogo>
                  <Logo>
                    <ImageWithFallback
                      layout="fill"
                      objectFit="cover"
                      placeholder="blur"
                      blurDataURL={rgbDataURL(
                        hexToRgb('ffffff')?.[0],
                        hexToRgb('ffffff')?.[1],
                        hexToRgb('ffffff')?.[2]
                      )}
                      src={getStrapiMedia(logo?.data?.attributes?.url ?? '')}
                      alt={logo?.data?.attributes?.alternativeText}
                      fallbackSrc={DEFAULT_IMAGE}
                      isPrerenderRequest={isPrerenderRequest}
                    />
                  </Logo>
                </LinkLogo>
              </Link>
            </SectionLogoCenter>

            <SectionRight>
              <RightMobileMenuStyled>
                <LocalizationSelect
                  onChange={(val: string | number) =>
                    onChangeLanguage(val.toString())
                  }
                  currentValue={locale}
                  options={
                    Object.values(availableInternationalization)
                      .filter((value) => {
                        return process.env.NEXT_PUBLIC_ENV === 'PROD'
                          ? Object.values(
                            notCurrentAvailableInternationalizationPROD
                          ).indexOf(value) === -1
                          : Object.values(
                            notCurrentAvailableInternationalization
                          ).indexOf(value) === -1;
                      })
                      .map((internationalization: string) => {
                        return {
                          label:
                            translations?.components?.shell?.header?.languages[
                              internationalization
                            ],
                          value: internationalization,
                        };
                      }) || []
                  }
                  type={SelectType.secondary}
                  textSize={TextSize.medium}
                />
                <LinkActionSubmenu
                  text={reservedArea?.text || ''}
                  title={reservedArea?.text || ''}
                  type={LinkType.headerSecondary}
                  size={TextSize.small}
                  icon={<PersonIcon />}
                  subLinks={reservedArea?.subLink}
                />
              </RightMobileMenuStyled>
            </SectionRight>
          </MobileBar>
        )}
      </StyledContentWrapper>

      <MegaMenuContainer open={megaMenuMainCategory !== ''}>
        <MegaMenu
          exitHover={exitHover}
          hover={hover}
          translations={translations}
          megaMenuMainCategory={megaMenuMainCategory}
          megamenuLinks={megamenuLinks}
          onMouseLeave={(e: any, touch = false) => {
            if (e.clientY >= 185 || touch) {
              setMegaMenuMainCategory('');
            }
          }}
          isPrerenderRequest={isPrerenderRequest}
        ></MegaMenu>
      </MegaMenuContainer>

      {/* Menu mobile */}
      {open && (
        <MenuMobileModal
          mainLinks={mainLinks}
          megamenuLinks={megamenuLinks}
          translations={translations}
          setOpen={setOpen}
          open={open}
          locale={locale}
        ></MenuMobileModal>
      )}

      {/* Reserved modal */}
      {reservedArea ? (
        <ReservedAreaModal
          open={openModal}
          handleClose={handleCloseModal}
          logo={logo}
          {...reservedArea}
        />
      ) : (
        <></>
      )}

      {/* Language modal */}
      <LocalizationModal
        open={openLocalizationModal}
        logo={logo}
        handleClose={handleCloseLocalizationModal}
        translations={translations}
        localizedPaths={localizedPaths}
        locale={locale}
        isPrerenderRequest={isPrerenderRequest}
      />

      {/* Search modal */}
      <SearchModal
        open={openSearchModal}
        logo={logo}
        handleClose={handleCloseSearchModal}
        translations={translations}
        localizedPaths={localizedPaths}
        locale={locale}
        megamenuLinks={megamenuLinks}
        userCountry={userCountry}
        isPrerenderRequest={isPrerenderRequest}
      />

      <SiteLinksSearchBoxJsonLd {...mapToSiteLinksSearchBoxJsonLd()} />
    </HeaderContainer>
  );
};

export default Header;
